import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationStateService } from '@core/services/navigation-state.service';
import { Store } from '@ngxs/store';
import { AnswerModuleClientRequest } from '@shared/models/answer-module-client-request';
import { AnswerModuleResponse } from '@shared/models/answer-module-response';
import { QuestionAnswerRequest } from '@shared/models/question-answer-request';
import { SetErrors } from '@shared/state/error.state';
import { ToastrService } from 'ngx-toastr';
import { Observable, tap } from 'rxjs';
import { ConfigService } from './config.service';
import { RedirectService } from './redirect.service';

@Injectable({
  providedIn: 'root',
})
export class AnswerModuleService {
  private readonly api_url = '';

  constructor(
    private httpClient: HttpClient,
    private toastService: ToastrService,
    private configService: ConfigService,
    private redirectService: RedirectService,
    private navigationStateService: NavigationStateService,
    private store: Store,
  ) {
    this.api_url = this.configService.get('ANSWER_SERVICE_API_URL');
  }

  postResponseSession(
    responseId: string,
    state: string,
    questionAnswer?: QuestionAnswerRequest[],
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    const body = {
      answers: questionAnswer,
    };

    return this.httpClient
      .post<AnswerModuleResponse>(
        this.api_url + '/api/response_session/' + responseId,
        body,
        { params: new HttpParams().set('state', state), observe: 'response' },
      )
      .pipe(
        tap({
          next: () => {},
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  postAnswer(
    responseId: string,
    stateId: string,
    answer: QuestionAnswerRequest,
  ): Observable<HttpResponse<any>> {
    const url = `${this.api_url}/api/response_session/${responseId}/state/${stateId}/answer`;
    return this.httpClient
      .post<any>(url, answer, {
        observe: 'response',
      })
      .pipe(
        tap({
          next: () => {},
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  getEditByResponseId(
    responseId: string,
    userId: string | null = null,
    sId: string | null = null,
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    let params = new HttpParams();
    if(userId){
      params = params.set('userId', userId);
    }
    if(sId){
      params = params.set('sId', sId);
    }
    return this.httpClient
      .get<AnswerModuleResponse>(
        this.api_url + '/api/edit/' + responseId, {
          observe: 'response',
          params: params,
        })
      .pipe(
        tap({
          next: () => {},
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  getInvitationByInvitationId(
    invitationId: string,
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    return this.httpClient
      .get<AnswerModuleResponse>(
        this.api_url + '/api/invitation/' + invitationId,
        { observe: 'response' },
      )
      .pipe(
        tap({
          next: () => {},
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  getInvitationByQuestId(
    questId: string,
    language: string | null = null,
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    let params = new HttpParams();
    if (language) {
      params = params.set('language', language);
    }
    return this.httpClient
      .get<AnswerModuleResponse>(this.api_url + '/api/bylink/' + questId, {
        observe: 'response',
        params: params,
      })
      .pipe(
        tap({
          next: () => {},
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  getPrintPreview(
    questId: string,
    showConditions: string,
    language: string | null,
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    const paramsOptions: { showConditions: string; language?: string } = {
      showConditions: showConditions,
    };
    if (language) {
      paramsOptions.language = language;
    }
    return this.httpClient
      .get<AnswerModuleResponse>(
        this.api_url + '/api/printpreview/' + questId,
        {
          observe: 'response',
          params: paramsOptions,
        },
      )
      .pipe(
        tap({
          next: () => {},
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  getNextState(
    responseId: string,
    currentState: string,
    requestBody: AnswerModuleClientRequest[],
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    const url =
      this.api_url +
      '/api/response_session/' +
      responseId +
      '/state/' +
      currentState +
      '/next';

    return this.httpClient
      .post<AnswerModuleResponse>(url, requestBody, {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        tap({
          next: (response) => {
            this.navigationStateService.updateFromResponse(response);
          },
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  startOver(
    endpointUrl: string,
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    const url = this.api_url + endpointUrl;
    return this.httpClient
      .post<AnswerModuleResponse>(url, [], {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        tap({
          next: (response) => {
            this.navigationStateService.setHideBackButton(
              response.body?.enableBackNavigation ? false : true,
            );
          },
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  checkPassword(
    responseId: string,
    currentState: string,
    requestBody: AnswerModuleClientRequest[],
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    const url =
      this.api_url +
      '/api/response_session/' +
      responseId +
      '/state/' +
      currentState +
      '/next';

    return this.httpClient
      .post<AnswerModuleResponse>(url, requestBody, {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        tap({
          next: () => {},
          error: (error: HttpErrorResponse) => {
            if (
              !error.error?.errors.find(
                (i: { errorType: string }) =>
                  i?.errorType === 'PasswordIncorrect',
              )
            ) {
              this.toastService.error(error.message, 'Error');
            }
            this.handleError(error);
          },
        }),
      );
  }

  getPreviousState(
    responseId: string,
    currentState: string,
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    const url = `${this.api_url}/api/response_session/${responseId}/state/${currentState}/previous`;

    return this.httpClient
      .post<AnswerModuleResponse>(url, null, {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        tap({
          next: (response) => {
            this.navigationStateService.updateFromResponse(response);
          },
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  postLanguage(
    responseId: string,
    currentState: string,
    requestBody: QuestionAnswerRequest,
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    const url =
      this.api_url +
      '/api/response_session/' +
      responseId +
      '/state/' +
      currentState +
      '/language';

    return this.httpClient
      .post<AnswerModuleResponse>(url, requestBody, {
        headers: {
          'Content-Type': 'application/json',
          accept: 'application/json',
        },
        observe: 'response',
      })
      .pipe(
        tap({
          next: () => {},
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  postCallback(
    callbackUrl: string,
    data: any,
  ): Observable<HttpResponse<AnswerModuleResponse>> {
    return this.httpClient
      .post<AnswerModuleResponse>(this.api_url + callbackUrl, data, {
        observe: 'response',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      .pipe(
        tap({
          next: () => {},
          error: (error: HttpErrorResponse) => {
            this.handleError(error);
          },
        }),
      );
  }

  handleError(httpErrorResponse: HttpErrorResponse) {
    if (httpErrorResponse.status === 400) {
      this.toastService.error(httpErrorResponse.message, 'Error');
    } else if (httpErrorResponse.status === 422) {
      this.store.dispatch(new SetErrors(httpErrorResponse.error.errors));
    } else {
      this.redirectService.redirectToErrorPage(httpErrorResponse.status);
    }
  }
}
